'use client';

import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { getCopy } from '@/lib/copy';
import { capturePostHogEvent, capturePostHogException } from '@/lib/posthog';
import { useEffect, useState } from 'react';

const R = '/_shared';

/**
 * A commit-phase `DOMException: NotFoundError` (React's `insertBefore` failing
 * because the reference sibling it held is no longer under its parent) is a
 * transient symptom of the DOM being rewritten out from under React - e.g. an
 * in-page translator swapping text nodes - not a persistent application fault.
 * Remounting the route rebuilds a clean tree, so we recover from it silently
 * instead of dead-ending the user on the manual "try again" screen.
 */
function isRecoverableCommitError(error: Error): boolean {
  const name = (error as { name?: string }).name ?? '';
  const message = error.message ?? '';
  return (
    name === 'NotFoundError' ||
    /NotFoundError|The object can ?not be found here|insertBefore/i.test(message)
  );
}

/**
 * A raw network `TypeError` (Chrome "network error" / "Failed to fetch",
 * Safari "Load failed", Firefox "NetworkError when attempting to fetch") that
 * reaches the boundary is a dropped connection - e.g. a server-component
 * payload stream cut after a mobile tab sat idle - not an application fault.
 * Our own API calls wrap these in `ApiClientError`, so only framework fetches
 * land here. `retry()` refetches the segment, which usually succeeds.
 */
function isTransientNetworkError(error: Error): boolean {
  return (
    error.name === 'TypeError' &&
    /network ?error|failed to fetch|load failed|network request failed|network connection was lost/i.test(error.message ?? '')
  );
}

type RecoverableKind = 'commit' | 'network';

function recoverableKind(error: Error): RecoverableKind | null {
  if (isRecoverableCommitError(error)) return 'commit';
  if (isTransientNetworkError(error)) return 'network';
  return null;
}

/** How long a fresh auto-recovery is suppressed after one is attempted. If the
 * boundary re-catches within this window the remount did not help, so we stop
 * and show the manual screen rather than loop. */
const AUTO_RECOVER_COOLDOWN_MS = 10_000;

// Module-scoped so it survives the error boundary remounting (component state
// does not): a recovery that immediately re-throws must not retry forever.
let lastAutoRecover: { at: number, kind: RecoverableKind } | null = null;

export default function ErrorPage({
  error,
  reset,
  retry,
}: {
  error: Error & { digest?: string }
  reset: () => void
  retry: () => void
}) {
  const kind = recoverableKind(error);
  // Decide once per mount whether this is a transient crash we can silently
  // remount past, or a fault the user must acknowledge.
  const [failedRecovery] = useState(() =>
    lastAutoRecover && Date.now() - lastAutoRecover.at < AUTO_RECOVER_COOLDOWN_MS ? lastAutoRecover : null,
  );
  const phase = kind && !failedRecovery ? 'recovering' : 'failed';
  // A network error needs a fresh server payload, not just a re-render.
  const recover = kind === 'network' ? retry : reset;

  useEffect(() => {
    capturePostHogException(error);
  }, [error]);

  useEffect(() => {
    if (!failedRecovery) return;
    capturePostHogEvent('error_boundary_auto_recover', {
      error_kind: failedRecovery.kind,
      outcome: 'manual_fallback',
    });
  }, [failedRecovery]);

  useEffect(() => {
    if (phase !== 'recovering' || !kind) return;
    lastAutoRecover = { at: Date.now(), kind };
    capturePostHogEvent('error_boundary_auto_recover', { error_kind: kind, outcome: 'attempted' });
    // Remount the route on the next frame so the current (failed) commit has
    // fully unwound before React rebuilds the tree.
    const raf = requestAnimationFrame(() => recover());
    return () => cancelAnimationFrame(raf);
  }, [phase, kind, recover]);

  if (phase === 'recovering') {
    // Brief, non-alarming placeholder while the route remounts itself.
    return (
      <div
        className="min-h-screen bg-background flex items-center justify-center"
        aria-busy="true"
        data-testid="error-auto-recovering"
      >
        <Logo size="md" showText animated={false} />
      </div>
    );
  }

  const isNetwork = kind === 'network';

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="max-w-md mx-auto px-4 py-12 text-center">
        <div className="mb-8 flex justify-center">
          <Logo size="md" showText animated={false} />
        </div>

        <h1 className="text-4xl font-bold tracking-tight mb-3">
          {getCopy(R, isNetwork ? 'error.network.title' : 'error.title')}
        </h1>
        <p className="text-muted-foreground mb-8">
          {isNetwork
            ? getCopy(R, 'apiError.network.unreachable')
            : error.message || getCopy(R, 'error.fallbackBody')}
        </p>

        <Button size="lg" onClick={recover}>
          {getCopy(R, 'error.tryAgain')}
        </Button>
      </div>
    </div>
  );
}

export { isRecoverableCommitError, isTransientNetworkError };
